// AnalyticsPanel.js

import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";

const AnalyticsPanel = () => {
  const backend_api_base_url = `${process.env.REACT_APP_SL_BE_BASE_ENV_PART}/${process.env.REACT_APP_STAGE_ENV}`;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [analyticsData, setAnalyticsData] = useState({
    messages: null,
    users: null
  });

  const meData = useSelector((state) => state.auth.meData);

  // Get tenant info safely
  const tenantInfo = meData?.tenant_info || {};
  
  // For AI messages: use tenant (string like "hisbullatenant")
  const aiMessagesTenantId = tenantInfo.tenant;
  
  // For site users: use tenant.id (string like "TEN-DYDHA")
  const usersTenantId = tenantInfo?.tenant?.id || tenantInfo.id;
  const appName = tenantInfo.app_name;

  console.log("Tenant Info:", tenantInfo);
  console.log("AI Messages Tenant ID:", aiMessagesTenantId);
  console.log("Users Tenant ID:", usersTenantId);
  console.log("App Name:", appName);

  useEffect(() => {
    const fetchAnalyticsData = async () => {
      if (!aiMessagesTenantId || !usersTenantId) {
        setError("Tenant information not available");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);

        // Fetch both AI messages and users data in parallel
        const [messagesResponse, usersResponse] = await Promise.all([
          fetch(
            `https://reporter2.${backend_api_base_url}/total_ai_msgs_tenant?tenant_id=${aiMessagesTenantId}`,
            {
              headers: {
                "x-api-key": "dGVuYW50MV9fU0VQUkFUT1JfX3Rlc3Rpbmcx"
              }
            }
          ),
          fetch(
            `https://reporter2.${backend_api_base_url}/total_users?tenant_id=${usersTenantId}`,
            {
              headers: {
                "x-api-key": "dGVuYW50MV9fU0VQUkFUT1JfX3Rlc3Rpbmcx"
              }
            }
          )
        ]);

        if (!messagesResponse.ok || !usersResponse.ok) {
          throw new Error(`API call failed: Messages: ${messagesResponse.status}, Users: ${usersResponse.status}`);
        }

        const messagesData = await messagesResponse.json();
        const usersData = await usersResponse.json();

        console.log("Messages data received:", messagesData);
        console.log("Users data received:", usersData);

        // Process messages data
        let processedMessages = null;
        if (messagesData.tenant_id) {
          processedMessages = {
            type: "tenant",
            tenantId: messagesData.tenant_id,
            totalMessages: messagesData.tenant_total_ai_msgs,
            globalTotal: messagesData.global_total_ai_msgs,
            region: messagesData.region,
            kbCount: messagesData.kbs_found,
            kbDetails: messagesData.kb_details
          };
        } else if (messagesData.kb_id) {
          processedMessages = {
            type: "kb",
            kbId: messagesData.kb_id,
            messages: messagesData.tenant_ai_msgs_count,
            globalTotal: messagesData.global_total_ai_msgs,
            region: messagesData.region
          };
        }

        // Process users data
        let processedUsers = null;
        if (usersData.tenant_id) {
          processedUsers = {
            type: "tenant",
            tenantId: usersData.tenant_id,
            totalUsers: usersData.total_tenant_users,
            apps: usersData.apps || [],
            appUsers: usersData.app_users
          };
        } else if (usersData.global_total_users !== undefined) {
          processedUsers = {
            type: "global",
            totalUsers: usersData.global_total_users
          };
        }

        setAnalyticsData({
          messages: processedMessages,
          users: processedUsers
        });
        setError(null);
      } catch (err) {
        console.error("Error fetching analytics:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchAnalyticsData();
  }, [aiMessagesTenantId, usersTenantId, backend_api_base_url]);

  // Format number with commas
  const formatNumber = (num) => {
    return num?.toLocaleString() || "0";
  };

  return (
    <div
      style={{
        background: "#fff",
        marginTop: "50px",
        padding: "20px"
      }}
    >
      {/* Analytics Stats Section */}
      <div style={{
        marginBottom: "30px",
        padding: "20px",
        background: "#f8f9fa",
        borderRadius: "8px",
        border: "1px solid #e9ecef"
      }}>
        <h2 style={{ marginBottom: "20px", color: "#333" }}>
          Platform Analytics Dashboard
          {aiMessagesTenantId && (
            <span style={{ fontSize: "14px", color: "#666", marginLeft: "10px" }}>
              (Tenant: {aiMessagesTenantId})
            </span>
          )}
        </h2>

        {loading && (
          <div style={{ textAlign: "center", padding: "20px" }}>
            <div>Loading analytics data...</div>
            <div style={{ fontSize: "12px", color: "#666", marginTop: "10px" }}>
              Fetching from {backend_api_base_url}
            </div>
          </div>
        )}

        {error && (
          <div style={{
            textAlign: "center",
            padding: "20px",
            color: "#dc3545",
            background: "#f8d7da",
            borderRadius: "4px"
          }}>
            <strong>Error loading analytics:</strong> {error}
          </div>
        )}

        {!loading && !error && (
          <div>
            {/* Stats Grid */}
            <div style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
              gap: "20px",
              marginBottom: "20px"
            }}>
              {/* AI Messages Card */}
              {analyticsData.messages && (
                <div style={{
                  background: "#fff",
                  padding: "20px",
                  borderRadius: "8px",
                  boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                  borderLeft: "4px solid #007bff"
                }}>
                  <h3 style={{ margin: "0 0 10px 0", color: "#666", fontSize: "14px", textTransform: "uppercase" }}>
                    AI Messages
                  </h3>
                  <p style={{
                    margin: "0",
                    fontSize: "36px",
                    fontWeight: "bold",
                    color: "#007bff",
                    lineHeight: "1.2"
                  }}>
                    {formatNumber(analyticsData.messages.totalMessages)}
                  </p>
                  <p style={{ margin: "5px 0 0 0", color: "#999", fontSize: "12px" }}>
                    Your tenant total
                  </p>
                  <hr style={{ margin: "15px 0" }} />
                  <div>
                    <span style={{ fontSize: "12px", color: "#666" }}>Global total: </span>
                    <strong>{formatNumber(analyticsData.messages.globalTotal)}</strong>
                  </div>
                  {analyticsData.messages.type === "tenant" && analyticsData.messages.kbCount > 0 && (
                    <p style={{ margin: "5px 0 0 0", color: "#999", fontSize: "12px" }}>
                      Across {analyticsData.messages.kbCount} knowledge base{analyticsData.messages.kbCount !== 1 ? 's' : ''}
                    </p>
                  )}
                </div>
              )}

              {/* Users Card */}
              {analyticsData.users && analyticsData.users.type !== "global" && (
                <div style={{
                  background: "#fff",
                  padding: "20px",
                  borderRadius: "8px",
                  boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                  borderLeft: "4px solid #28a745"
                }}>
                  <h3 style={{ margin: "0 0 10px 0", color: "#666", fontSize: "14px", textTransform: "uppercase" }}>
                    Active Users
                  </h3>
                  <p style={{
                    margin: "0",
                    fontSize: "36px",
                    fontWeight: "bold",
                    color: "#28a745",
                    lineHeight: "1.2"
                  }}>
                    {formatNumber(analyticsData.users.totalUsers)}
                  </p>
                  <p style={{ margin: "5px 0 0 0", color: "#999", fontSize: "12px" }}>
                    Total users in your tenant
                  </p>
                  {analyticsData.users.apps && analyticsData.users.apps.length > 0 && (
                    <div style={{ marginTop: "15px" }}>
                      <div style={{ fontSize: "12px", color: "#666", marginBottom: "5px" }}>
                        Per Application:
                      </div>
                      {analyticsData.users.apps.map((app, idx) => (
                        <div key={idx} style={{ 
                          display: "flex", 
                          justifyContent: "space-between",
                          fontSize: "12px",
                          padding: "2px 0"
                        }}>
                          <span>{app.app_name}:</span>
                          <strong>{formatNumber(app.user_count)}</strong>
                        </div>
                      ))}
                    </div>
                  )}
                  {analyticsData.users.appUsers !== undefined && (
                    <div style={{ marginTop: "15px" }}>
                      <div style={{ fontSize: "12px", color: "#666" }}>
                        Current app users: <strong>{formatNumber(analyticsData.users.appUsers)}</strong>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* KB Details Section */}
            {analyticsData.messages?.type === "tenant" && 
             analyticsData.messages.kbDetails && 
             analyticsData.messages.kbDetails.length > 0 && (
              <div style={{
                marginTop: "30px",
                padding: "20px",
                background: "#fff",
                borderRadius: "8px",
                border: "1px solid #e9ecef"
              }}>
                <h3 style={{ margin: "0 0 15px 0", color: "#333", fontSize: "18px" }}>
                  Knowledge Base Breakdown
                </h3>
                <div style={{ overflowX: "auto" }}>
                  <table style={{
                    width: "100%",
                    borderCollapse: "collapse"
                  }}>
                    <thead>
                      <tr style={{ borderBottom: "2px solid #e9ecef" }}>
                        <th style={{ textAlign: "left", padding: "10px", color: "#666" }}>Knowledge Base ID</th>
                        <th style={{ textAlign: "right", padding: "10px", color: "#666" }}>Message Count</th>
                       </tr>
                    </thead>
                    <tbody>
                      {analyticsData.messages.kbDetails.map((kb, index) => (
                        <tr key={kb.kb_id} style={{
                          borderBottom: "1px solid #e9ecef",
                          background: index % 2 === 0 ? "#f8f9fa" : "#fff"
                        }}>
                          <td style={{ padding: "10px", fontFamily: "monospace", fontSize: "12px" }}>
                            {kb.kb_id}
                          </td>
                          <td style={{ textAlign: "right", padding: "10px", fontWeight: "bold" }}>
                            {formatNumber(kb.count)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Refresh Button */}
            <div style={{ marginTop: "20px", textAlign: "right" }}>
              <button
                onClick={() => {
                  setLoading(true);
                  // Re-fetch will happen in useEffect
                }}
                style={{
                  padding: "8px 16px",
                  background: "#007bff",
                  color: "#fff",
                  border: "none",
                  borderRadius: "4px",
                  cursor: "pointer",
                  fontSize: "14px"
                }}
              >
                Refresh Data
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AnalyticsPanel;